import { FC, useEffect, useState } from 'react';
import { SpaceFileMimeType } from 'shared';

import Loader from '../../../../components/loader/index';
import { csvToGrid, SheetGrid } from './sheet-grid.util';
import { ExcelWorksheet, worksheetToGrid } from './workbook-grid.util';

/**
 * A spreadsheet as the file itself draws it — 20.2.3 for `.xlsx` and `.csv`.
 *
 * Read in the page, which is why there is no converter behind this: `exceljs` already ships in
 * this repo for writing exports, and its browser bundle reads a workbook — values, colours,
 * fonts, borders, merges and column widths — without the Node core modules CRA does not
 * polyfill. The exports this product writes set every colour explicitly, so one of them previews
 * as the document it is rather than as a grey grid. A CSV needs no library at all.
 *
 * The bytes are handed in rather than fetched back off the object URL: the viewer owns that URL
 * and revokes it when the reader moves on, and a reader racing it would fail on a file it
 * already had.
 *
 * What does not come across: charts, images and conditional formatting, which live outside the
 * cell. The Download button is beside it for whoever needs the real thing.
 */
export const FilePreviewSheet: FC<{
  blob: Blob;
  contentType: string;
  onError: () => void;
  zoom: number;
}> = ({ blob, contentType, onError, zoom }) => {
  const [sheets, setSheets] = useState<null | SheetGrid[]>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    const read = async (): Promise<SheetGrid[]> => {
      if (contentType === SpaceFileMimeType.CSV || contentType === SpaceFileMimeType.PLAIN_TEXT) {
        return [csvToGrid(await blob.text())];
      }

      // The browser bundle by path: the package's main entry is the Node build, which CRA cannot
      // resolve without polyfills.
      const { Workbook } = await import('exceljs/dist/exceljs.min.js');
      const workbook = new Workbook();

      await workbook.xlsx.load(await blob.arrayBuffer());

      return workbook.worksheets.map((sheet) =>
        worksheetToGrid(sheet as unknown as ExcelWorksheet),
      );
    };

    void read()
      .then((read) => {
        if (isCurrent) {
          setSheets(read);
          setActive(0);
        }
      })
      // A workbook the reader cannot parse is said out loud, where an empty grid read as a file
      // with nothing in it.
      .catch(() => {
        if (isCurrent) onError();
      });

    return () => {
      isCurrent = false;
    };
  }, [blob, contentType, onError]);

  if (!sheets) return <Loader />;

  const grid = sheets[active];

  return (
    <div className="flex h-fit flex-col items-start gap-3">
      {/* Only when there is a choice to make: one sheet needs no tab above it. */}
      {sheets.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {sheets.map((sheet, index) => (
            <button
              className={`rounded-md px-3 py-1 text-label-sm font-medium ${
                index === active ? 'bg-white text-gray-700' : 'bg-gray-700 text-white'
              }`}
              key={sheet.name}
              onClick={() => setActive(index)}
              type="button"
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
      {/* Zoom scales the whole sheet, not the type: the column widths are the file's own, in
          pixels, and scaling the text alone would leave them behind. */}
      <div
        className="w-fit bg-white"
        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
      >
        <table className="border-collapse text-[11pt] text-gray-700">
          <colgroup>
            {grid?.columns.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <tbody>
            {grid?.rows.map((row, rowIndex) => (
              <tr key={rowIndex} style={{ height: row.height }}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    className="px-1.5"
                    colSpan={cell.colSpan}
                    key={cellIndex}
                    rowSpan={cell.rowSpan}
                    style={cell.style}
                  >
                    {cell.text}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
